import { TYPE_LABEL } from '../constants/machineTypes';

// Human labels for machines: `rassini-SANDCORE-01` → `Sand Core 01`.
//
// ⚠️ DISPLAY ONLY — never feed these back into anything addressable.
//
// The company prefix is a VERIFICATION INSTRUMENT, not noise. Scoping bugs are judged by the machine
// name precisely because the client doesn't control it: a check that compared the company slug the
// server reported could pass by agreeing with itself, whereas `rassini-` appearing in a Chentronix
// view is proof. That is how one tenant's machines were caught leaking into another's floor. FOCAS
// also key on the raw name. So the prefix must survive in `machine`/`name`, in the route
// param, and in the React key — only the rendered text loses it.

/**
 * Mirror of `server/src/utils/machineDisplayName.js`. Duplicated rather than fetched because the
 * `/floor/live` read model returns the raw name with no `displayName` field, and one asymmetric
 * payload is a worse problem than eight lines of shared convention.
 *
 * `'default'` is exempt: that fleet is named `LATHE-01` with no prefix at all (one of the four
 * distinct things `'default'` means in this codebase).
 */
export function stripCompanyPrefix(name, companySlug) {
  const n = String(name || '');
  if (companySlug && companySlug !== 'default' && n.startsWith(`${companySlug}-`)) {
    return n.slice(companySlug.length + 1);
  }
  return n;
}

/** `SANDCORE` → `Sandcore`. Only reached when machineType is unknown, so it stays deliberately dumb. */
function prettifyStem(stem) {
  return String(stem || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * @param {string} name        raw machine name, e.g. `rassini-LATHE-01`
 * @param {string} companySlug company being viewed, e.g. `rassini`
 * @param {string} [machineType] `Machine.machineType`; preferred over the name's stem when known
 * @returns {string} e.g. `Lathe 01`
 */
export function machineLabel(name, companySlug, machineType) {
  const bare = stripCompanyPrefix(name, companySlug);
  // Split a trailing unit number off the stem, keeping its zero padding: `01` reads as a machine
  // number, `1` reads as a count.
  const m = bare.match(/^(.*?)[-_ ]?(\d+)$/);
  const stem = m ? m[1] : bare;
  const num = m ? m[2] : '';

  // A well-formed stem is ONE token (`LATHE`, `SANDCORE`, `SWISS_LATHE`). A hyphen surviving here
  // means a segment we did not expect — most usefully, a company prefix that did NOT match the
  // company being viewed, i.e. a foreign machine on this floor. Falling back to the raw stem keeps
  // that tell on screen (`Rassini Lathe 01`), because substituting the tidy type label would erase
  // the one visible symptom of a cross-tenant leak.
  const anomalous = stem.includes('-');
  const typeWord = TYPE_LABEL[String(machineType || '').toUpperCase()];
  const word = (!anomalous && typeWord) || prettifyStem(stem) || bare;
  return num ? `${word} ${num}` : word;
}
